import {
  useRescheduleInterviewMutation,
  useScheduleInterviewMutation,
} from "@/features/dashBoard/candidatesInterviewsApi";
import { useGetJobByIdQuery } from "@/features/dashBoard/getJobsApi";
import {
  Button,
  CustomMultiBadgeInput,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InputFormField,
  Label,
  Spinner,
  Textarea,
  TimePicker,
  TimezoneSelect,
  Typography,
} from "@cointervue/design-system";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import type { ScheduleInterviewProps, InterviewFormValues } from "./types";
import {
  addMinutesToTime,
  combineDateAndTimeToUTC,
  getAllTimeZones,
  getErrorMessage,
  getFutureTime,
  getTimeZoneDetails,
  getTodayInTimeZone,
  getUserTimeZone,
} from "@cointervue/shared";

const ALL_TIMEZONES = getAllTimeZones();

// --- Helper Functions ---

function formatPreviousSlot(
  slot?: string,
  candidateTz?: string,
): string | undefined {
  if (!slot) return undefined;
  if (slot.includes("GMT")) {
    return slot.replace(/\(?(GMT[+-]\d{2}:\d{2})\)?\s*[\w/ -]*/, "($1)");
  }
  if (!candidateTz) return slot;
  return `${slot} | (${getTimeZoneDetails(candidateTz).offset})`;
}

function getInitialFormValues(
  initialValues?: Partial<InterviewFormValues>,
  defaultTz = "UTC",
  maxDuration = 45,
): InterviewFormValues {
  const tz = initialValues?.candidateTz || defaultTz;
  const todayTz = getTodayInTimeZone(tz);
  const startTime = initialValues?.startTime || getFutureTime(10) || "02:00";
  const endTime =
    initialValues?.endTime ||
    addMinutesToTime(startTime, maxDuration) ||
    "03:00";
  const date =
    initialValues?.date && initialValues.date >= todayTz
      ? initialValues.date
      : todayTz;

  return {
    date: date || undefined,
    startTime,
    endTime,
    candidateTz: tz,
    notes: initialValues?.notes ?? "",
    interviewId: initialValues?.interviewId ?? "",
  };
}

function getShiftedTimes(
  prevTz: string,
  newTz: string,
  startTime: string | undefined,
  endTime: string | undefined,
  date: Date | undefined,
  maxDuration: number,
) {
  const diffMinutes =
    getTimeZoneDetails(newTz).offsetMinutes -
    getTimeZoneDetails(prevTz).offsetMinutes;
  if (!diffMinutes) return null;

  let shiftedDate: Date | undefined;
  let shiftedStart: string | undefined;
  let shiftedEnd: string | undefined;

  if (startTime) {
    shiftedStart = addMinutesToTime(startTime, diffMinutes);
    shiftedEnd = addMinutesToTime(shiftedStart, maxDuration);
    if (date) {
      const [h, m] = startTime.split(":").map(Number);
      const dt = new Date(date);
      dt.setHours(h || 0, (m || 0) + diffMinutes, 0, 0);
      dt.setHours(0, 0, 0, 0);
      shiftedDate = dt;
    }
  } else if (endTime) {
    shiftedEnd = addMinutesToTime(endTime, diffMinutes);
  }

  return { shiftedStart, shiftedEnd, shiftedDate };
}

function checkPastTime(
  date: Date | undefined,
  startTime: string | undefined,
  tz: string,
  isDateInPast: boolean,
): string | undefined {
  if (!date || !startTime || isDateInPast) return undefined;
  const startUtc = combineDateAndTimeToUTC(date, startTime, tz);
  if (!startUtc) return undefined;
  if (new Date(startUtc).getTime() < Math.floor(Date.now() / 60000) * 60000) {
    return "Interview time cannot be in the past";
  }
  return undefined;
}

function validateInterviewDate(
  date: Date | undefined,
  timeZone?: string,
): string | true {
  if (!date) return "Date is required";
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const todayForTz = getTodayInTimeZone(timeZone || "UTC");
  if (d.getTime() < todayForTz.getTime()) {
    return "Interview date cannot be in the past";
  }
  return true;
}

function DateLabel({ selectedTzLabel }: { selectedTzLabel: string }) {
  return (
    <span className="flex items-center gap-1.5 flex-wrap">
      <span>Date</span>
      <span className="text-xs font-normal text-muted-foreground">
        {selectedTzLabel}
      </span>
    </span>
  );
}

// --- Main Component ---

const ScheduleInterview = memo(function ScheduleInterview({
  open,
  onClose,
  mode,
  candidateId,
  candidateName,
  interviewerEmail,
  previousSlot,
  initialValues,
  isLoading: externalLoading = false,
  panelMember,
  onSuccess,
  jobId,
}: ScheduleInterviewProps) {
  const { data: jobData, isLoading: isJobLoading } = useGetJobByIdQuery(
    jobId ?? "",
    { skip: !jobId || !open },
  );
  const job = jobData?.job;
  const isFetchingData = externalLoading || isJobLoading;
  const maxInterviewDuration =
    job?.interviewDuration?.maxInterviewDuration || 45;

  const [emails, setEmails] = useState<string[]>([]);
  const defaultTz = useMemo(() => getUserTimeZone(), []);

  const [scheduleInterview, { isLoading: isScheduling }] =
    useScheduleInterviewMutation();
  const [rescheduleInterview, { isLoading: isRescheduling }] =
    useRescheduleInterviewMutation();
  const isLoading = mode === "schedule" ? isScheduling : isRescheduling;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { isValid, errors },
  } = useForm<InterviewFormValues>({
    mode: "onChange",
    defaultValues: {
      date: undefined,
      startTime: "",
      endTime: "",
      candidateTz: defaultTz,
      notes: "",
      interviewId: "",
    },
  });

  const [startTime, endTime, date, candidateTz] = useWatch({
    control,
    name: ["startTime", "endTime", "date", "candidateTz"],
  });

  const activeTz = candidateTz || defaultTz;
  const today = useMemo(() => getTodayInTimeZone(activeTz), [activeTz]);
  const selectedTzLabel = useMemo(
    () => `(${getTimeZoneDetails(activeTz).offset})`,
    [activeTz],
  );
  const displayPreviousSlot = useMemo(
    () => formatPreviousSlot(previousSlot, initialValues?.candidateTz),
    [previousSlot, initialValues?.candidateTz],
  );

  const prevTzRef = useRef<string>(defaultTz);
  const isDateInPast = Boolean(
    date && new Date(date).setHours(0, 0, 0, 0) < today.getTime(),
  );

  const timeRangeError =
    startTime && endTime && startTime >= endTime
      ? "Start time must be before end time"
      : undefined;

  const pastTimeError = useMemo(
    () => checkPastTime(date, startTime, activeTz, isDateInPast),
    [date, startTime, activeTz, isDateInPast],
  );

  const timeErrorMessage =
    errors.startTime?.message ||
    errors.endTime?.message ||
    timeRangeError ||
    pastTimeError;

  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (isFetchingData) return;

    const init = getInitialFormValues(
      initialValues,
      defaultTz,
      maxInterviewDuration,
    );
    prevTzRef.current = init.candidateTz;
    reset(init);
    setEmails(
      mode === "reschedule"
        ? (panelMember?.map((m) => m.email) ?? [])
        : interviewerEmail
          ? [interviewerEmail]
          : [],
    );
  }, [
    open,
    isFetchingData,
    mode,
    initialValues,
    interviewerEmail,
    reset,
    panelMember,
    defaultTz,
    maxInterviewDuration,
  ]);

  const handleTimezoneChange = useCallback(
    (newTz: string) => {
      const prevTz = prevTzRef.current || defaultTz;
      prevTzRef.current = newTz;
      if (prevTz === newTz) return;

      const shift = getShiftedTimes(
        prevTz,
        newTz,
        getValues("startTime"),
        getValues("endTime"),
        getValues("date"),
        maxInterviewDuration,
      );

      if (!shift) return;

      if (shift.shiftedStart) {
        setValue("startTime", shift.shiftedStart, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (shift.shiftedEnd) {
        setValue("endTime", shift.shiftedEnd, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (shift.shiftedDate) {
        setValue("date", shift.shiftedDate, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      trigger(["startTime", "endTime", "date", "candidateTz"]);
    },
    [defaultTz, getValues, setValue, trigger, maxInterviewDuration],
  );

  const handleStartTimeChange = useCallback(
    (time: string | null) => {
      setValue("startTime", time || "", {
        shouldValidate: true,
        shouldDirty: true,
      });

      if (time) {
        const calculatedEndTime = addMinutesToTime(time, maxInterviewDuration);
        if (calculatedEndTime) {
          setValue("endTime", calculatedEndTime, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    },
    [maxInterviewDuration, setValue],
  );

  const handleCancel = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) onClose();
    },
    [onClose],
  );

  const onSubmit = useCallback(
    async (values: InterviewFormValues) => {
      if (!values.date) return;
      if (timeRangeError || pastTimeError || isDateInPast) return;

      const timeZone = values.candidateTz || defaultTz;
      const startUTC = combineDateAndTimeToUTC(
        values.date,
        values.startTime,
        timeZone,
      );
      const endUTC = combineDateAndTimeToUTC(
        values.date,
        values.endTime,
        timeZone,
      );

      const payload = {
        mode: "ai" as const,
        scheduledDate: startUTC,
        panelMembers: emails.map((email) => ({
          email,
          type: "panel" as const,
        })),
        time: { startTime: startUTC, endTime: endUTC },
        candidateTz: timeZone,
        notes: values.notes.trim() ? [values.notes.trim()] : undefined,
      };

      try {
        if (mode === "schedule") {
          await scheduleInterview({ ...payload, candidateId }).unwrap();
        } else {
          const interviewId = initialValues?.interviewId;
          if (!interviewId) {
            if (!isMountedRef.current) return;
            toast.error("Cannot reschedule: missing interview ID");
            return;
          }
          await rescheduleInterview({ ...payload, id: interviewId }).unwrap();
        }
        if (!isMountedRef.current) return;

        toast.success(
          mode === "schedule"
            ? "Interview scheduled successfully"
            : "Interview rescheduled successfully",
        );

        onSuccess?.();
        reset();
        onClose();
      } catch (error) {
        if (!isMountedRef.current) return;
        toast.error(getErrorMessage(error));
      }
    },
    [
      emails,
      mode,
      onSuccess,
      reset,
      onClose,
      scheduleInterview,
      candidateId,
      initialValues?.interviewId,
      rescheduleInterview,
      defaultTz,
      timeRangeError,
      pastTimeError,
      isDateInPast,
    ],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-5 max-w-[calc(100%-2rem)] sm:max-w-2xl max-h-[95vh] overflow-y-auto"
        disableCloseButton={isLoading || isFetchingData}
        disableOutsideClose={true}
      >
        {isFetchingData && (
          <div className="absolute inset-0 z-50 rounded-2xl flex items-center justify-center bg-background/70 backdrop-blur-sm">
            <Spinner className="size-10 text-primary" />
          </div>
        )}
        <DialogHeader>
          <DialogTitle>
            {mode === "schedule"
              ? "Schedule Interview"
              : "Reschedule Interview"}
          </DialogTitle>
          <DialogDescription>
            {mode === "schedule"
              ? "Set the interview date for the candidate"
              : "Update the interview details"}
          </DialogDescription>
        </DialogHeader>

        {mode === "reschedule" && displayPreviousSlot && (
          <div className="rounded-md bg-muted px-3 py-1.5 text-xs sm:text-sm">
            <strong>Previous Slot:</strong> {displayPreviousSlot}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <InputFormField
              label="Interview Mode"
              required
              value="AI Interview"
              disabled
              className="w-full"
            />
            <InputFormField
              label="Candidate Name"
              required
              value={candidateName}
              disabled
              className="w-full"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 items-start gap-4 sm:gap-6">
            <Controller
              name="date"
              control={control}
              rules={{
                required: "Date is required",
                validate: (val) =>
                  validateInterviewDate(
                    val,
                    getValues("candidateTz") || defaultTz,
                  ),
              }}
              render={({ field, fieldState: { error } }) => (
                <div className="space-y-1">
                  <DatePicker
                    label={<DateLabel selectedTzLabel={selectedTzLabel} />}
                    required
                    disabled={isLoading}
                    value={field.value}
                    onChange={field.onChange}
                    calendarProps={{
                      today,
                      disabled: { before: today },
                      fromDate: today,
                    }}
                  />
                  {error?.message && (
                    <Typography variant="error">{error.message}</Typography>
                  )}
                </div>
              )}
            />

            <Controller
              name="candidateTz"
              control={control}
              rules={{ required: "Candidate time zone is required" }}
              render={({ field, fieldState: { error } }) => (
                <TimezoneSelect
                  label="Candidate Time Zone"
                  required
                  timeZones={ALL_TIMEZONES}
                  value={field.value}
                  onChange={(newTz) => {
                    field.onChange(newTz);
                    handleTimezoneChange(newTz);
                  }}
                  disabled={isLoading}
                  error={error}
                />
              )}
            />
          </div>

          <div className="space-y-1 flex flex-col">
            <Label className="text-sm font-medium flex gap-1">
              <span className="flex items-center gap-1.5 flex-wrap">
                <span>Time</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {selectedTzLabel}
                </span>
                <span className="text-destructive text-xl leading-none">*</span>
              </span>

              {job?.interviewDuration && (
                <Typography variant="bodySmall">
                  <span className="text-brand-gray ml-2">
                    ({job.interviewDuration.minInterviewDuration} -
                    {job.interviewDuration.maxInterviewDuration} mins)
                  </span>
                </Typography>
              )}
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="startTime"
                control={control}
                rules={{ required: "Start time is required" }}
                render={({ field }) => (
                  <TimePicker
                    format="hh:mm a"
                    disabled={isLoading}
                    value={field.value}
                    onChange={handleStartTimeChange}
                    placeholder="Start time"
                  />
                )}
              />

              <Controller
                name="endTime"
                control={control}
                rules={{ required: "End time is required" }}
                render={({ field }) => (
                  <TimePicker
                    format="hh:mm a"
                    disabled={isLoading}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="End time"
                  />
                )}
              />
            </div>
            {timeErrorMessage && (
              <Typography variant="error">{timeErrorMessage}</Typography>
            )}
          </div>

          <div className="space-y-1">
            <Label>
              Panel Members
              <span className="text-muted-foreground">(Optional)</span>
            </Label>

            <CustomMultiBadgeInput
              value={emails}
              onChange={setEmails}
              disabled={isLoading}
              placeholder="Enter panel email and press Enter"
            />
          </div>

          <div className="space-y-1">
            <Label>Notes for Panel or AI</Label>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  disabled={isLoading}
                  placeholder="Add any additional information for the interview panel..."
                />
              )}
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2.5 sm:gap-3 pt-4">
            <Button
              type="submit"
              loading={isLoading}
              disabled={
                !isValid ||
                Boolean(timeRangeError || pastTimeError || isDateInPast) ||
                isLoading
              }
            >
              {mode === "schedule"
                ? "Schedule Interview"
                : "Reschedule Interview"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
});

export default ScheduleInterview;
